import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import MCQLogic from "../../SubtopicsPage/MCQLogic";
import { CodeBlock } from "../../CodeOutputBlocks";

const questionsData = [
  {
    question: (
      <div>
        <p>What is the output?</p>
        <CodeBlock
          language="python"
          code={`name = "Python"\nprint(type(name))`}
        />
      </div>
    ),
    options: [
      "<class 'str'>",
      "<class 'int'>",
      "<class 'float'>",
      "<class 'bool'>",
    ],
    answer: "<class 'str'>",
  },
  {
    question: (
      <div>
        <p>
          What data type is <b>age</b>?
        </p>
        <CodeBlock language="python" code={`age = 21\nprint(type(age))`} />
      </div>
    ),
    options: [
      "<class 'str'>",
      "<class 'int'>",
      "<class 'float'>",
      "<class 'bool'>",
    ],
    answer: "<class 'int'>",
  },
  {
    question: (
      <div>
        <p>
          What is the type of <b>price</b>?
        </p>
        <CodeBlock
          language="python"
          code={`price = 599.99\nprint(type(price))`}
        />
      </div>
    ),
    options: [
      "<class 'str'>",
      "<class 'int'>",
      "<class 'float'>",
      "<class 'bool'>",
    ],
    answer: "<class 'float'>",
  },
  {
    question: (
      <div>
        <p>What will this print?</p>
        <CodeBlock
          language="python"
          code={`is_valid = True\nprint(type(is_valid))`}
        />
      </div>
    ),
    options: [
      "<class 'str'>",
      "<class 'int'>",
      "<class 'float'>",
      "<class 'bool'>",
    ],
    answer: "<class 'bool'>",
  },
  {
    question: (
      <div>
        <p>
          Identify the data types of <b>a</b> and <b>b</b>:
        </p>
        <CodeBlock
          language="python"
          code={`a = "100"\nb = 100\nprint(type(a))\nprint(type(b))`}
        />
      </div>
    ),
    options: [
      "<class 'str'> and <class 'int'>",
      "<class 'int'> and <class 'str'>",
      "<class 'int'> and <class 'int'>",
      "<class 'str'> and <class 'str'>",
    ],
    answer: "<class 'str'> and <class 'int'>",
  },
  {
    question: (
      <div>
        <p>
          What are the types of <b>x</b> and <b>y</b>?
        </p>
        <CodeBlock
          language="python"
          code={`x = False\ny = 3.14\nprint(type(x))\nprint(type(y))`}
        />
      </div>
    ),
    options: [
      "<class 'bool'> and <class 'float'>",
      "<class 'float'> and <class 'bool'>",
      "<class 'int'> and <class 'float'>",
      "<class 'bool'> and <class 'int'>",
    ],
    answer: "<class 'bool'> and <class 'float'>",
  },
  {
    question: (
      <div>
        <p>Which line contains a Float value?</p>
        <CodeBlock
          language="python"
          code={`score = 95\nprice = 199.99\ntemperature = -5.5\nname = "Alex"`}
        />
      </div>
    ),
    options: [
      "score = 95",
      "price = 199.99",
      "temperature = -5.5",
      "Both 2 and 3",
    ],
    answer: "Both 2 and 3",
  },
  {
    question: (
      <div>
        <p>What will be the output?</p>
        <CodeBlock
          language="python"
          code={`message = 'Welcome to Python!'\nprint(type(message))`}
        />
      </div>
    ),
    options: ["<class 'str'>", "<class 'int'>", "<class 'float'>", "String"],
    answer: "<class 'str'>",
  },
  {
    question: (
      <div>
        <p>Which of these creates a Boolean variable?</p>
        <CodeBlock
          language="python"
          code={`is_active = True\nstatus = "True"\nflag = False`}
        />
      </div>
    ),
    options: ["Only line 1", "Only line 2", "Lines 1 and 3", "All three lines"],
    answer: "Lines 1 and 3",
  },
  {
    question: (
      <div>
        <p>
          What is the data type of <b>quantity</b>?
        </p>
        <CodeBlock
          language="python"
          code={`quantity = "50"\nprint(type(quantity))`}
        />
      </div>
    ),
    options: ["Integer", "String", "Float", "Boolean"],
    answer: "String",
    // Because it's in quotes
  },

  {
    question: "Which of these is a correct Boolean value in Python?",
    options: ["true", "FALSE", "True", "Bool"],
    answer: "True",
  },
  {
    question:
      "How do you assign the string 'Hello' to a variable named greeting?",
    options: [
      "greeting = Hello",
      "greeting = 'Hello'",
      'greeting = "Hello',
      "greeting = (Hello)",
    ],
    answer: "greeting = 'Hello'",
  },
  {
    question: "What is the '=' symbol called in Python?",
    options: [
      "Equal to operator",
      "Assignment Operator",
      "Comparison operator",
      "Equality sign",
    ],
    answer: "Assignment Operator",
  },
  {
    question: "Which of the following is NOT a valid String in Python?",
    options: ['"Python123"', "'hello@123'", "no_quotes", '"True"'],
    answer: "no_quotes",
  },
  {
    question: "Which data type is used for decimal numbers like 3.14 or 99.99?",
    options: ["Integer", "String", "Float", "Boolean"],
    answer: "Float",
  },
];

const Variables_DT_MCQ = ({ subtopicId, goalName, courseName, onComplete }) => {
  const { markSubtopicComplete, loadProgressSummary, completedContent } =
    useAuth();

  const [isCompleted, setIsCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const randomQuestions = [...questionsData].sort(() => Math.random() - 0.5);

  // Check if subtopic is already completed
  useEffect(() => {
    if (subtopicId && completedContent.includes(subtopicId)) {
      setIsCompleted(true);
    }
  }, [completedContent, subtopicId]);

  const handleCompletion = async () => {
    if (isLoading || isCompleted) return;

    try {
      setIsLoading(true);

      // Validate that we have the required parameters
      if (!subtopicId) {
        console.error("❌ Subtopic ID is required");
        alert("Error: Subtopic ID is missing");
        return;
      }

      console.log("🎯 Marking subtopic complete:", {
        subtopicId,
        goalName,
        courseName,
      });

      const result = await markSubtopicComplete(
        subtopicId,
        goalName || "Goal 1",
        courseName || "Static Website: HTML CSS & Bootstrap"
      );

      if (result.success) {
        await loadProgressSummary();
        setIsCompleted(true);
        console.log("✅ MCQ successfully marked as completed");

        // Call the parent completion handler if provided
        if (onComplete) {
          onComplete();
        }
      } else {
        console.error("❌ Failed to mark MCQ complete:", result.message);
        alert(`Failed to mark as complete: ${result.message}`);
      }
    } catch (error) {
      console.error("❌ Failed to mark MCQ complete:", error);
      alert("Failed to mark as complete. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MCQLogic
      title="Variables and Data Types - MCQs"
      questions={randomQuestions}
      isCompleted={isCompleted}
      isLoading={isLoading}
      onComplete={handleCompletion}
      subtopicId={subtopicId}
      goalName={goalName}
      courseName={courseName}
    />
  );
};

export default Variables_DT_MCQ;
